"use client";

import { useRouter, useSearchParams } from "next/navigation";
import {
	Component,
	type ErrorInfo,
	type ReactNode,
	useEffect,
	useRef,
	useState,
} from "react";
import { NoiseOverlay } from "@/components/custom/noise_overlay";
import { PRICING_TABS } from "@/components/pricing/pricing_tabs";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trackGoogleAdsEvent } from "@/lib/google-ads-events";
import {
	buildHomePricingHref,
	normalizePricingTab,
	PRICING_SECTION_ID,
	type PricingTab,
	parseCheckoutPlanId,
} from "@/lib/pricing-navigation";
import {
	groupPricingRecords,
	type PricingData,
	type PricingRecord,
} from "@/lib/pricing-record";
import {
	getCloudflareTracePricingCountry,
	normalizePricingCountry,
} from "@/lib/pricing-region";
import AuditPricingTab from "./audit_pricing_tab";
import EditingPricingTab from "./editing_pricing_tab";
import EngagementPricingTab from "./engagement_pricing_tab";
import GrowthPricingTab from "./growth_pricing_tab";
import { PricingSectionShell } from "./pricing_section_skeleton";
import ReachPricingTab from "./reach_pricing_tab";
import TargetedPricingTab from "./targeted_pricing_tab";

interface PricingApiResponse {
	meta?: {
		country?: string;
	};
	records?: PricingRecord[];
	pricingUnavailable?: boolean;
}

interface PricingSectionProps {
	initialCountry?: string;
	sectionId?: string;
}

function scheduleIdleWork(callback: () => void) {
	let didRun = false;
	const runOnce = () => {
		if (didRun) return;
		didRun = true;
		callback();
	};

	const fallbackId = globalThis.setTimeout(runOnce, 2200);

	if ("requestIdleCallback" in window) {
		const idleId = window.requestIdleCallback(runOnce, { timeout: 2500 });
		return () => {
			globalThis.clearTimeout(fallbackId);
			window.cancelIdleCallback?.(idleId);
		};
	}

	return () => globalThis.clearTimeout(fallbackId);
}

interface PricingTabErrorBoundaryProps {
	tab: string;
	planCount: number;
	children: ReactNode;
}

interface PricingTabErrorBoundaryState {
	hasError: boolean;
}

class PricingTabErrorBoundary extends Component<
	PricingTabErrorBoundaryProps,
	PricingTabErrorBoundaryState
> {
	state: PricingTabErrorBoundaryState = {
		hasError: false,
	};

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		if (process.env.NODE_ENV === "development") {
			console.error("[PricingSection] Failed tab render", {
				tab: this.props.tab,
				planCount: this.props.planCount,
				error,
				errorInfo,
			});
		}
	}

	reset = () => {
		this.setState({ hasError: false });
	};

	render() {
		const { hasError } = this.state;

		if (hasError) {
			return (
				<section className="rounded-2xl border border-red-200 bg-red-50 p-10 text-center">
					<h3 className="font-syne font-black text-3xl uppercase tracking-tight text-red-700">
						Tab temporarily unavailable
					</h3>
					<p className="mt-3 text-sm text-red-600">
						We couldn&apos;t render the {this.props.tab} pricing section right
						now. This can happen if your network changes while switching tabs.
					</p>
					<button
						type="button"
						onClick={this.reset}
						className="mt-6 rounded-xl border border-red-200 text-red-700 px-4 py-2 font-syne font-bold text-xs uppercase tracking-wide"
					>
						Try loading this tab again
					</button>
					<p className="mt-4 text-xs text-red-500">
						{`Visible plans: ${this.props.planCount}`}
					</p>
				</section>
			);
		}

		return this.props.children;
	}
}

interface PricingTabsContentProps {
	pricingData: PricingData;
	country: string;
	activeTab: PricingTab;
	checkoutPlanId: number | null;
}

function PricingTabsContent({
	pricingData,
	country,
	activeTab,
	checkoutPlanId,
}: PricingTabsContentProps) {
	const getInitialCheckoutPlanId = (tab: PricingTab) =>
		activeTab === tab ? checkoutPlanId : null;

	return (
		<div className="w-full px-4 md:px-8 min-h-[50vh] relative">
			<TabsContent
				value="reach"
				className="mt-0 w-full outline-none data-[state=inactive]:hidden data-[state=active]:animate-in data-[state=active]:fade-in data-[state=active]:slide-in-from-bottom-2 duration-300"
			>
				<PricingTabErrorBoundary
					tab="Reach"
					planCount={pricingData.views?.length ?? 0}
				>
					<ReachPricingTab
						data={pricingData.views}
						country={country}
						initialCheckoutPlanId={getInitialCheckoutPlanId("reach")}
					/>
				</PricingTabErrorBoundary>
			</TabsContent>
			<TabsContent
				value="growth"
				className="mt-0 w-full outline-none data-[state=inactive]:hidden data-[state=active]:animate-in data-[state=active]:fade-in data-[state=active]:slide-in-from-bottom-2 duration-300"
			>
				<PricingTabErrorBoundary
					tab="Growth"
					planCount={pricingData.subscriptions?.length ?? 0}
				>
					<GrowthPricingTab
						data={pricingData.subscriptions}
						country={country}
						initialCheckoutPlanId={getInitialCheckoutPlanId("growth")}
					/>
				</PricingTabErrorBoundary>
			</TabsContent>
			<TabsContent
				value="engagement"
				className="mt-0 w-full outline-none data-[state=inactive]:hidden data-[state=active]:animate-in data-[state=active]:fade-in data-[state=active]:slide-in-from-bottom-2 duration-300"
			>
				<PricingTabErrorBoundary
					tab="Watch Hour"
					planCount={pricingData.monetization?.length ?? 0}
				>
					<EngagementPricingTab
						data={pricingData.monetization}
						country={country}
						initialCheckoutPlanId={getInitialCheckoutPlanId("engagement")}
					/>
				</PricingTabErrorBoundary>
			</TabsContent>
			<TabsContent
				value="editing"
				className="mt-0 w-full outline-none data-[state=inactive]:hidden data-[state=active]:animate-in data-[state=active]:fade-in data-[state=active]:slide-in-from-bottom-2 duration-300"
			>
				<PricingTabErrorBoundary
					tab="Editing"
					planCount={pricingData.editor?.length ?? 0}
				>
					<EditingPricingTab data={pricingData.editor} country={country} />
				</PricingTabErrorBoundary>
			</TabsContent>
			<TabsContent
				value="audit"
				className="mt-0 w-full outline-none data-[state=inactive]:hidden data-[state=active]:animate-in data-[state=active]:fade-in data-[state=active]:slide-in-from-bottom-2 duration-300"
			>
				<PricingTabErrorBoundary
					tab="Audit"
					planCount={pricingData.audit?.length ?? 0}
				>
					<AuditPricingTab data={pricingData.audit} country={country} />
				</PricingTabErrorBoundary>
			</TabsContent>
			<TabsContent
				value="targeted"
				className="mt-0 w-full outline-none data-[state=inactive]:hidden data-[state=active]:animate-in data-[state=active]:fade-in data-[state=active]:slide-in-from-bottom-2 duration-300"
			>
				<PricingTabErrorBoundary tab="Targeted Ads" planCount={0}>
					<TargetedPricingTab />
				</PricingTabErrorBoundary>
			</TabsContent>
		</div>
	);
}

export default function PricingSection({
	initialCountry = "usd",
	sectionId = PRICING_SECTION_ID,
}: PricingSectionProps) {
	const router = useRouter();
	const searchParams = useSearchParams();
	const sectionRef = useRef<HTMLElement>(null);
	const [activeTab, setActiveTab] = useState(() =>
		normalizePricingTab(searchParams.get("tab")),
	);
	const activeTabRef = useRef(activeTab);
	const hasTrackedPricingViewRef = useRef(false);
	const checkoutPlanId = parseCheckoutPlanId(searchParams.get("plan"));
	const [pricingData, setPricingData] = useState<PricingData | null>(null);
	const [country, setCountry] = useState(() =>
		normalizePricingCountry(initialCountry),
	);

	useEffect(() => {
		activeTabRef.current = activeTab;
	}, [activeTab]);

	useEffect(() => {
		if (window.location.hash !== `#${sectionId}`) return;

		const section = sectionRef.current;
		if (!section) return;

		const scrollToPricing = () => {
			section.scrollIntoView({ behavior: "smooth", block: "start" });
		};

		if ("requestAnimationFrame" in window) {
			const animationFrameId = window.requestAnimationFrame(scrollToPricing);
			return () => window.cancelAnimationFrame(animationFrameId);
		}

		const timeoutId = globalThis.setTimeout(scrollToPricing, 0);
		return () => globalThis.clearTimeout(timeoutId);
	}, [sectionId]);

	useEffect(() => {
		const controller = new AbortController();
		let timeout: number | null = null;
		let didLoadPricing = false;

		async function loadPricing() {
			if (didLoadPricing) return;
			didLoadPricing = true;
			timeout = window.setTimeout(() => controller.abort(), 10000);

			try {
				const response = await fetch("/api/pricing", {
					cache: "no-store",
					headers: { accept: "application/json" },
					signal: controller.signal,
				});
				if (!response.ok) throw new Error("Pricing request failed");

				const payload = (await response.json()) as PricingApiResponse;
				const traceCountry = await getCloudflareTracePricingCountry(
					controller.signal,
				);
				const nextCountry =
					traceCountry ?? normalizePricingCountry(payload.meta?.country);
				if (Array.isArray(payload.records)) {
					setPricingData(groupPricingRecords(payload.records));
				}
				setCountry(nextCountry);
			} catch {
				if (!controller.signal.aborted) {
					setPricingData(null);
					setCountry(normalizePricingCountry(initialCountry));
				}
			} finally {
				if (timeout !== null) window.clearTimeout(timeout);
			}
		}

		const section = sectionRef.current;
		let observer: IntersectionObserver | null = null;

		if (section && "IntersectionObserver" in window) {
			observer = new IntersectionObserver((entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					if (!hasTrackedPricingViewRef.current) {
						hasTrackedPricingViewRef.current = true;
						trackGoogleAdsEvent("view_item_list", {
							item_list_id: activeTabRef.current,
							item_list_name: `pricing_${activeTabRef.current}`,
						});
					}
					loadPricing();
					observer?.disconnect();
				}
			});

			observer.observe(section);
		}

		const cancelIdleWork = scheduleIdleWork(loadPricing);

		return () => {
			observer?.disconnect();
			cancelIdleWork();
			if (timeout !== null) window.clearTimeout(timeout);
			controller.abort();
		};
	}, [initialCountry]);

	const handleTabChange = (val: string) => {
		const tab = normalizePricingTab(val);
		if (tab === activeTabRef.current) {
			return;
		}
		const previousTab = activeTabRef.current;

		activeTabRef.current = tab;
		setActiveTab(tab);
		trackGoogleAdsEvent("view_item_list", {
			item_list_id: tab,
			item_list_name: `pricing_${tab}`,
		});

		const params = new URLSearchParams(window.location.search);
		params.delete("plan");
		const nextHref = buildHomePricingHref({ extraParams: params, tab });
		const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;

		if (nextHref === currentHref) {
			return;
		}

		if (process.env.NODE_ENV === "development") {
			const pricingState = pricingData
				? {
						reach: pricingData.views?.length ?? 0,
						growth: pricingData.subscriptions?.length ?? 0,
						engagement: pricingData.monetization?.length ?? 0,
						editing: pricingData.editor?.length ?? 0,
						audit: pricingData.audit?.length ?? 0,
					}
				: null;

			console.debug("[PricingSection] switching tab", {
				from: previousTab,
				to: tab,
				plan: checkoutPlanId,
				pricingState,
			});
		}

		try {
			router.replace(nextHref, { scroll: false });
		} catch (error) {
			if (process.env.NODE_ENV === "development") {
				console.error("[PricingSection] Failed tab navigation", error);
			}
		}
	};

	if (!pricingData) {
		return (
			<PricingSectionShell
				activeTab={activeTab}
				sectionId={sectionId}
				sectionRef={sectionRef}
				onTabChange={handleTabChange}
			/>
		);
	}

	return (
		<section
			ref={sectionRef}
			id={sectionId}
			className="scroll-mt-20 isolate bg-white py-20 text-zinc-900 font-sans w-full relative overflow-hidden selection:bg-black selection:text-white md:py-24"
			data-testid="pricing-section"
		>
			<NoiseOverlay />
			<div className="absolute inset-0 z-0 pointer-events-none">
				<div className="absolute top-0 right-0 w-[800px] h-[800px] bg-linear-to-br from-zinc-50 to-transparent opacity-60 rounded-full blur-3xl" />
			</div>

			<div
				className="relative z-10 w-full max-w-[1600px] mx-auto flex flex-col"
				data-testid="pricing-section-stack"
			>
				<div className="text-center mb-12 px-4">
					<div className="animate-on-scroll-view">
						<h2 className="font-syne font-bold text-4xl md:text-6xl uppercase tracking-tighter leading-none mb-6 text-zinc-950">
							Pricing
						</h2>
						<p className="font-inter text-zinc-500 text-sm md:text-base max-w-xl mx-auto leading-relaxed font-medium">
							Transparent investments for exponential digital growth.
						</p>
					</div>
				</div>

				<Tabs
					value={activeTab}
					className="w-full flex flex-col items-center"
					onValueChange={handleTabChange}
				>
					<div className="w-full flex justify-center mb-8 md:mb-8 relative z-40 px-4 md:px-0">
						<TabsList className="bg-zinc-100/80 backdrop-blur-md border border-zinc-200/50 shadow-inner grid grid-cols-2 w-full gap-2 rounded-3xl p-2 h-auto md:inline-flex md:w-auto md:gap-1 md:rounded-full! md:p-1.5">
							{PRICING_TABS.map((tab) => {
								return (
									<TabsTrigger
										key={tab.value}
										value={tab.value}
										data-pricing-tab-value={tab.value}
										className={`relative font-inter font-semibold uppercase tracking-wide transition-[color,background-color,box-shadow] duration-300
                    text-zinc-900 hover:text-zinc-950
                    data-[state=active]:text-red-600 data-[state=active]:shadow-sm
                    data-[state=active]:bg-white
                    flex items-center gap-2
                    w-full justify-center px-2 py-3 text-xs rounded-xl
                    md:w-auto md:px-6 md:py-2.5 md:rounded-full md:text-sm`}
									>
										<span
											className="w-4 h-4 shrink-0 flex items-center justify-center"
											data-pricing-icon="true"
											data-pricing-icon-key={tab.iconKey}
										>
											{tab.renderIcon("text-red-600")}
										</span>
										{tab.label}
									</TabsTrigger>
								);
							})}
						</TabsList>
					</div>

					<PricingTabsContent
						pricingData={pricingData}
						country={country}
						activeTab={activeTab}
						checkoutPlanId={checkoutPlanId}
					/>
				</Tabs>
			</div>
		</section>
	);
}
